import {ApiClient} from './ApiClient.js';
import {Logs} from './Logs.js';
import {Notification} from './Notification.js';
import {Storage} from './Storage.js';
import {UIForm} from '../ui/UIForm.js';
import {UIFormElement} from '../ui/UIFormElement.js';
import {UIModal} from '../ui/UIModal.js';
import {translate} from '../modules/i18n.js';

export class Account {

    constructor(options = {}) {
        new Logs("message", ["ACCOUNT CONSTRUCTOR"]);

        this.app = options.app || null;
        this.api = this.resolveApiClient(options);
        this.mode = options.mode || "web";
        this.platform = options.platform || "web";
        this.storage = options.storage || Storage;
        this.authToken = options.authToken || null;
        this.endpoints = {
            login: "/auth/login",
            create: "/users/insert",
            validateAccount: "/users/valid",
            update: "/users/update",
            updatePreferences: "/preferences/set",
            changePassword: "/users/password/update",
            recoverPasswordRequest: "/users/recover1",
            recoverPasswordConfirm: "/users/recover2",
            delete: "/users/delete",
            organisations: "/organismes/list",
            createOrganisation: "/organismes/insert",
            validateOrganisationMember: "/users/organisme/valid",
            organisationMembers: "/users/members",
            currentUser: "/users/info",
            check: "/users/check",
            refresh: "/auth/refresh",
            ...options.endpoints
        };
        this.storageKeys = {
            token: "token",
            accessToken: "accessToken",
            deviceId: "deviceId",
            user: "accountUser",
            ...options.storageKeys
        };
        this.modals = {};
        this.forms = {};
        this.pendingCreateAccount = null;
        this.pendingPasswordRecoveryEmail = null;
        this.postFireAuthContext = false;
        this.postFireMapDisplayValue = null;
    }

    resolveApiClient(options = {}) {
        new Logs("message", ["ACCOUNT RESOLVE API CLIENT"]);

        if (options.api instanceof ApiClient) {
            return options.api;
        }

        if (options.api && typeof options.api.query === "function") {
            return options.api;
        }

        if (options.apiConfig) {
            return new ApiClient(options.apiConfig, this.app);
        }

        return null;
    }

    static init(options = {}) {
        new Logs("message", ["ACCOUNT INIT"]);

        return new Account(options);
    }

    async connect(credentials) {
        new Logs("message", ["ACCOUNT CONNECT"]);

        const data = await this.withDeviceAuthData(credentials);
        const response = await this.query("post", this.endpoints.login, this.withAuthToken(data));
        const token = response && response.result ? response.result.token : null;

        if (token) {
            await this.storeToken(token);
        }

        await this.storeAuthTokens(response);

        return response;
    }

    renderAuthenticationChoice() {
        new Logs("message", ["ACCOUNT RENDER AUTHENTICATION CHOICE"]);
        this.requireAppUI();

        if (this.app.user) {
            new Logs("warning", ["User already connected. stop here."]);
            return false;
        }

        document.querySelectorAll("body .ui-modal").forEach((modal) => modal.remove());
        document.getElementById("startup")?.classList.add("hide");

        this.modals.authenticationChoice = new UIModal({
            container: "#ui-container",
            close: this.canCloseAuthenticationModal(),
            class: "account ui-modal-fullscreen ui-modal-vertical-center",
            id: "account_authentication_choice",
            icon: "logo.svg",
            title: "Bienvenue",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false
        });

        const container = this.getModalContentZone("account_authentication_choice");
        const choiceContainer = document.createElement("div");
        choiceContainer.classList.add("account-authentication-choice");

        const createChoice = this.createAuthenticationChoiceBlock({
            explain: "<strong>Créez votre compte en 30s</strong><br>Accédez à la carte, géolocalisez-vous, ajoutez des photos et contribuez à la protection des milieux aquatiques.",
            buttonId: "account_choice_create",
            buttonLabel: "Je crée mon compte",
            buttonIcon: "/images/signup-grey.svg",
            onClick: () => {
                this.modals.authenticationChoice.remove();
                this.renderCreateAccount();
            }
        });

        const separator = document.createElement("div");
        separator.classList.add("choice-or");
        separator.textContent = "ou";

        const connectChoice = this.createAuthenticationChoiceBlock({
            explain: "<strong>Vous avez déjà un compte ?</strong><br>Connectez-vous avec votre adresse email.",
            buttonId: "account_choice_connect",
            buttonLabel: "Je me connecte",
            buttonIcon: "/images/connect-grey.svg",
            onClick: () => {
                this.modals.authenticationChoice.remove();
                this.renderConnect();
            }
        });

        choiceContainer.append(createChoice, separator, connectChoice);
        container?.append(choiceContainer);

        this.modals.authenticationChoice.show();
        return true;
    }

    canCloseAuthenticationModal() {
        new Logs("message", ["ACCOUNT CAN CLOSE AUTHENTICATION MODAL"]);

        return this.mode !== "native" && this.app?.forceAuthenticationOnBoot !== true;
    }

    getModalContentZone(modalId) {
        new Logs("message", ["ACCOUNT GET MODAL CONTENT ZONE"]);

        const selector = UIModal.normalizeId(modalId);
        const content = document.querySelector(`${selector} .ui-modal-content`);

        if (!content) {
            return null;
        }

        let zoneContent = content.querySelector(":scope > .ui-modal-zonecontent");

        if (!zoneContent) {
            zoneContent = document.createElement("div");
            zoneContent.classList.add("ui-modal-zonecontent");
            content.append(zoneContent);
        }

        return zoneContent;
    }

    getModalContentZoneSelector(modalId) {
        new Logs("message", ["ACCOUNT GET MODAL CONTENT ZONE SELECTOR"]);

        this.getModalContentZone(modalId);

        return `${UIModal.normalizeId(modalId)} .ui-modal-content .ui-modal-zonecontent`;
    }

    createAuthenticationChoiceBlock(options) {
        new Logs("message", ["ACCOUNT CREATE AUTHENTICATION CHOICE BLOCK"]);

        const choice = document.createElement("div");
        choice.classList.add("choice");

        const explain = document.createElement("div");
        explain.classList.add("choice-explain");
        explain.innerHTML = options.explain;

        const button = document.createElement("button");
        button.type = "button";
        button.id = options.buttonId;
        button.classList.add("ui-buttons", "big");
        button.addEventListener("click", options.onClick);

        const icon = document.createElement("img");
        icon.src = options.buttonIcon;
        icon.alt = "";

        button.append(icon, document.createTextNode(options.buttonLabel));

        choice.append(explain, button);

        return choice;
    }

    renderConnect() {
        new Logs("message", ["ACCOUNT RENDER CONNECT"]);
        this.requireAppUI();

        if (this.app.user) {
            new Logs("warning", ["User already connected. stop here."]);
            return false;
        }

        // REMOVE ALL MODAL
        $("body .ui-modal").remove();

        // HIDE LOADING
        $('#startup').addClass("hide");

        // HIDE UI
        this.app.hideUI();

        // REMOVE PREVIOUS ELEMENT
        document.getElementById("account_connect")?.remove();

        this.modals.connect = new UIModal({
            container: "#ui-container",
            close: this.canCloseAuthenticationModal(),
            class : "account ui-modal-fullscreen ui-modal-vertical-center",
            id: "account_connect",
            icon: "connect-white.svg",
            title: "Connectez-vous",
            undertitle: "Choisissez une méthode de connexion",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false
        });

        this.setupAuthModalContext("account_connect");
        document.querySelector("#account_connect > .close")?.addEventListener("click", () => {
            //this.app.showUI();
        });

        this.setConnectBackButton(() => {
            this.modals.connect.remove();
            this.renderAuthenticationChoice();
        }, {
            hideInPostFireChoice: true
        });

        const methods = this.getConnectionMethods();
        const container = document.createElement("div");
        container.classList.add("account-create-methods", "account-connect-methods");
        this.getModalContentZone("account_connect")?.append(container);

        if (methods.includes("email")) {
            container.append(this.createAccountMethodButton({
                className: "email",
                icon: "/images/mail-grey.svg",
                label: "Avec mon adresse email",
                onClick: () => this.renderConnectEmail()
            }));
        }

        window.setTimeout(() => this.modals.connect?.show(), 800);
        return true;
    }

    renderConnectEmail(options = {}) {
        new Logs("message", ["ACCOUNT RENDER CONNECT EMAIL"]);

        this.getModalContentZone("account_connect")?.replaceChildren();

        const modalUnderline = document.querySelector("#account_connect .undertitle");
        if (modalUnderline) modalUnderline.textContent = "Saisissez votre adresse email et votre mot de passe";


        this.setConnectBackButton(() => {
            this.renderConnect();
        });

        const container = this.getModalContentZoneSelector("account_connect");
        this.forms.connect = new UIForm({
            app: this.app,
            container,
            form: {
                id: "form_account_connect",
                submit: () => this.submitConnect(),
                content: [
                    {
                        type: "email",
                        label: "Email",
                        name: "email",
                        value: options.email || null,
                        required: true
                    },
                    {
                        type: "password",
                        label: "Mot de passe",
                        name: "pass",
                        required: true
                    }
                ],
                buttons: [
                    {
                        type: "submit",
                        label: "Se connecter",
                        class: "big"
                    }
                ]
            }
        });

        const recovery = document.createElement("button");
        recovery.type = "button";
        recovery.classList.add("ui-buttons", "linktype");
        recovery.textContent = "Mot de passe oublié";
        recovery.addEventListener("click", () => {
            const form = document.getElementById("form_account_connect");
            const email = form ? new FormData(form).get("email") : null;
            this.renderPasswordRecoveryRequest({
                email
            });
        });

        this.getModalContentZone("account_connect")?.append(recovery);

        return true;
    }

    setConnectBackButton(onClick, options = {}) {
        new Logs("message", ["ACCOUNT SET CONNECT BACK BUTTON"]);

        const modal = document.getElementById("account_connect");

        document.querySelectorAll("#account_connect > .account-create-back").forEach((element) => element.remove());

        const back = document.createElement("div");
        back.classList.add("account-create-back");
        back.setAttribute("role", "button");
        back.setAttribute("aria-label", "Retour");

        const backIcon = document.createElement("img");
        backIcon.src = "/images/back-white.svg";
        backIcon.alt = "";
        back.append(backIcon);
        back.addEventListener("click", onClick);
        modal?.append(back);
        this.applyAuthModalNavigationMode("account_connect", options);

        return back;
    }

    async submitConnect() {
        new Logs("message", ["ACCOUNT SUBMIT CONNECT"]);

        this.modals.connect.loadingShow();

        try {
            const response = await this.connect(new FormData(document.getElementById("form_account_connect")));
            this.modals.connect.loadingHide();

            if (this.isSuccess(response)) {
                this.modals.connect.remove();
                await this.renderAuthenticationSuccess(response);
                return response;
            }

            return this.notifyError(response, "Erreur de connexion");
        } catch (error) {
            this.modals.connect.loadingHide();
            return this.notifyException(error, "Erreur de connexion");
        }
    }

    renderCreateAccount() {
        new Logs("message", ["ACCOUNT RENDER CREATE"]);
        this.requireAppUI();

        // REMOVE PREVIOUS ELEMENT
        document.getElementById("account_create")?.remove();

        // HIDE UI
        this.app.hideUI();

        this.modals.create = new UIModal({
            container: "#ui-container",
            close: this.canCloseAuthenticationModal(),
            class: "account ui-modal-fullscreen ui-modal-vertical-center",
            id: "account_create",
            icon: "signup-white.svg",
            title: "Créer mon compte",
            undertitle: "Choisissez une méthode de création",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false
        });

        this.setupAuthModalContext("account_create");
        document.querySelector("#account_create > .close")?.addEventListener("click", () => {
            //this.app.showUI();
        });

        const modal = document.getElementById("account_create");
        const back = document.createElement("div");
        back.classList.add("account-create-back");
        back.setAttribute("role", "button");
        back.setAttribute("aria-label", "Retour");

        const backIcon = document.createElement("img");
        backIcon.src = "/images/back-white.svg";
        backIcon.alt = "";
        back.append(backIcon);
        back.addEventListener("click", () => {
            this.modals.create.remove();
            this.renderAuthenticationChoice();
        });
        modal?.append(back);
        this.applyAuthModalNavigationMode("account_create", {
            hideInPostFireChoice: true
        });

        const methods = this.getCreationMethods();
        const container = document.createElement("div");
        container.classList.add("account-create-methods");
        this.getModalContentZone("account_create")?.append(container);

        if (methods.includes("email")) {
            container.append(this.createAccountMethodButton({
                className: "email",
                icon: "/images/mail-grey.svg",
                label: "Avec mon adresse email",
                onClick: () => this.renderCreateAccountEmail()
            }));
        }

        window.setTimeout(() => this.modals.create?.show(), 800);
        return true;
    }

    createAccountMethodButton(options) {
        new Logs("message", ["ACCOUNT CREATE ACCOUNT METHOD BUTTON"]);

        const button = document.createElement("button");
        button.type = "button";
        button.classList.add("ui-buttons", "big");

        if(options.noIconFilter) {
            button.classList.add("no-icon-filter");
        }

        if (options.icon) {
            const icon = document.createElement("img");
            icon.src = options.icon;
            icon.alt = "";
            button.append(icon);
        }

        button.append(document.createTextNode(options.label));

        button.addEventListener("click", options.onClick);

        return button;
    }

    renderCreateAccountEmail() {
        new Logs("message", ["ACCOUNT RENDER CREATE ACCOUNT EMAIL"]);

        this.pendingCreateAccount = null;
        this.getModalContentZone("account_create")?.replaceChildren();

        const modal = document.getElementById("account_create");

        const modal_underline = document.querySelector("#account_create .undertitle");
        if (modal_underline) modal_underline.textContent = "Merci de remplir le formulaire";

        const back = document.createElement("div");
        document.querySelectorAll("#account_create > .account-create-back").forEach((element) => element.remove());
        back.classList.add("account-create-back");
        back.setAttribute("role", "button");
        back.setAttribute("aria-label", "Retour");

        const backIcon = document.createElement("img");
        backIcon.src = "/images/back-white.svg";
        backIcon.alt = "";
        back.append(backIcon);
        back.addEventListener("click", () => {
            this.renderCreateAccount();
        });
        modal?.append(back);
        this.applyAuthModalNavigationMode("account_create");


        this.app.UIForm(this.getModalContentZoneSelector("account_create"), {
            id: "form_account_create_email",
            submit: () => this.submitCreateAccountEmailCheck(),
            content: [
                {
                    type: "text",
                    label: "Prénom",
                    name: "prenom",
                    value: "Test",
                    class: "ui-form-element-width-50",
                    required: true
                },
                {
                    type: "text",
                    label: "Nom",
                    name: "nom",
                    value: "USER",
                    class: "ui-form-element-width-50",
                    required: true
                },
                {
                    type: "email",
                    label: "Email",
                    name: "email",
                    value: "test-user@geopeche.com",
                    required: true
                },
                {
                    type: "password",
                    label: "Mot de passe",
                    name: "pass",
                    value: "Test-Test-666",
                    autocomplete: "new-password",
                    required: true
                }
            ],
            buttons: [
                {
                    type: "submit",
                    label: "Suivant",
                    class: "big"
                }
            ]
        });
    }

    async submitCreateAccountEmailCheck() {
        new Logs("message", ["ACCOUNT SUBMIT CREATE ACCOUNT EMAIL CHECK"]);

        this.app.UIModalLoadingShow("#account_create");

        try {
            const accountData = this.normalizeCreateAccountData(this.formToObject("form_account_create_email"));
            const response = await this.createAccount(accountData);
            this.app.UIModalLoadingHide("#account_create");

            if (this.isSuccess(response)) {
                this.pendingCreateAccount = {
                    ...accountData,
                    uid: this.extractCreateAccountUid(response)
                };
                this.renderCreateAccountEmailValidation(response);
                return response;
            }

            return this.notifyError(response, "Création du compte");
        } catch (error) {
            this.app.UIModalLoadingHide("#account_create");
            return this.notifyException(error, "Création du compte");
        }
    }

    renderCreateAccountEmailValidation(response = null) {
        new Logs("message", ["ACCOUNT RENDER CREATE ACCOUNT EMAIL VALIDATION"]);

        this.getModalContentZone("account_create")?.replaceChildren();

        const modal = document.getElementById("account_create");
        const modalUnderline = document.querySelector("#account_create .undertitle");

        if (modalUnderline) {
            modalUnderline.textContent = "Validez votre adresse email";
        }

        document.querySelectorAll("#account_create > .account-create-back").forEach((element) => element.remove());

        const back = document.createElement("div");
        back.classList.add("account-create-back");
        back.setAttribute("role", "button");
        back.setAttribute("aria-label", "Retour");

        const backIcon = document.createElement("img");
        backIcon.src = "/images/back-white.svg";
        backIcon.alt = "";
        back.append(backIcon);
        back.addEventListener("click", () => {
            this.renderCreateAccountEmail();
        });
        modal?.append(back);
        this.applyAuthModalNavigationMode("account_create");

        if (!this.pendingCreateAccount?.uid) {
            new Logs("warning", ["Create account validation requested without uid", response]);
            this.notifyError({
                message: "Le serveur n'a pas retourné l'identifiant de validation du compte."
            }, "Création du compte");
            this.renderCreateAccountEmail();
            return false;
        }

        const validationForm = {
            id: "form_account_create_email_validation",
            submit: () => this.submitCreateAccountEmailValidation(),
            content: [
                {
                    type: "message",
                    text: "Ne fermez pas cet écran et indiquez ci-dessous le code de validation qui vous a été envoyé par mail."
                },
                {
                    type: "validation_code",
                    label: "Code de validation",
                    name: "code",
                    count: 6,
                    format: ["integer", "integer", "integer", "integer", "integer", "integer"],
                    required: true
                }
            ],
            buttons: [
                {
                    type: "submit",
                    label: "Valider",
                    class: "big"
                }
            ]
        };

        this.forms.createAccountEmailValidation = validationForm;
        this.app.UIForm(this.getModalContentZoneSelector("account_create"), validationForm);

        return true;
    }

    async submitCreateAccountEmailValidation() {
        new Logs("message", ["ACCOUNT SUBMIT CREATE ACCOUNT EMAIL VALIDATION"]);

        const form = this.formToObject("form_account_create_email_validation");
        const code = this.normalizeCreateAccountValidationCode(form.code);
        const uid = this.pendingCreateAccount?.uid || null;
        const validationCodeConfig = this.getFormElementConfig(this.forms.createAccountEmailValidation, "code");

        if (!uid || !this.isValidCreateAccountValidationCode(code, validationCodeConfig)) {
            new Notification({
                icon: "warning.svg",
                title: "Code de validation",
                content: "Le code de validation est incomplet ou invalide."
            });
            return false;
        }

        this.app.UIModalLoadingShow("#account_create");

        try {
            const response = await this.validateCreateAccountEmail(uid, code);
            this.app.UIModalLoadingHide("#account_create");

            if (this.isSuccess(response)) {
                await this.storeAuthTokens(response);
                this.createUserSuccess(response);
                return response;
            }

            return this.notifyError(response, "Validation du compte");
        } catch (error) {
            this.app.UIModalLoadingHide("#account_create");
            return this.notifyException(error, "Validation du compte");
        }
    }

    isPostFireAuthModalContext() {
        new Logs("message", ["ACCOUNT IS POST FIRE AUTH MODAL CONTEXT"]);

        return this.mode === "web" && this.app?.hasFired === true;
    }

    setupAuthModalContext(modalId) {
        new Logs("message", ["ACCOUNT SETUP AUTH MODAL CONTEXT", modalId]);

        this.postFireAuthContext = this.isPostFireAuthModalContext();
        this.applyAuthModalNavigationMode(modalId);

        if (!this.postFireAuthContext) {
            return false;
        }

        document.body.classList.add("vapor");
        this.setMapVisibleForAuthModal(false);
        this.bindAuthModalCloseCleanup(modalId);

        return true;
    }

    applyAuthModalNavigationMode(modalId, options = {}) {
        new Logs("message", ["ACCOUNT APPLY AUTH MODAL NAVIGATION MODE", modalId, this.postFireAuthContext, options]);

        const modal = document.getElementById(modalId);
        if (!modal) {
            return false;
        }

        const close = modal.querySelector(":scope > .close");
        const back = modal.querySelector(":scope > .account-create-back");

        if (this.postFireAuthContext) {
            if (close) {
                close.style.display = "flex";
            }
            if (back) {
                back.style.display = options.hideInPostFireChoice ? "none" : "";
            }
        } else {
            if (close) {
                close.style.display = "";
            }
            if (back) {
                back.style.display = "";
            }
        }

        return true;
    }

    bindAuthModalCloseCleanup(modalId) {
        new Logs("message", ["ACCOUNT BIND AUTH MODAL CLOSE CLEANUP", modalId]);

        const modal = document.getElementById(modalId);
        const close = modal?.querySelector(":scope > .close");

        if (!close) {
            return false;
        }

        close.addEventListener("click", () => {
            this.cleanupPostFireAuthContext();
        });

        return true;
    }

    setMapVisibleForAuthModal(visible) {
        new Logs("message", ["ACCOUNT SET MAP VISIBLE FOR AUTH MODAL", visible]);

        const appMethod = visible ? "showMap" : "hideMap";
        if (typeof this.app?.[appMethod] === "function") {
            this.app[appMethod]();
            return true;
        }

        const map = document.getElementById("map");
        if (!map) {
            return false;
        }

        if (!visible) {
            this.postFireMapDisplayValue = map.style.display;
            map.style.display = "none";
            return true;
        }

        map.style.display = this.postFireMapDisplayValue ?? "";
        this.postFireMapDisplayValue = null;
        return true;
    }

    cleanupPostFireAuthContext() {
        new Logs("message", ["ACCOUNT CLEANUP POST FIRE AUTH CONTEXT"]);

        if (!this.postFireAuthContext) {
            return false;
        }

        //document.body.classList.remove("vapor");
        this.setMapVisibleForAuthModal(true);
        this.postFireAuthContext = false;

        return true;
    }

    renderPasswordRecoveryRequest(options = {}) {
        new Logs("message", ["ACCOUNT RENDER PASSWORD RECOVERY REQUEST"]);
        this.requireAppUI();

        const email = options.email || this.pendingPasswordRecoveryEmail || null;
        this.pendingPasswordRecoveryEmail = email;

        document.getElementById("account_recover_request")?.remove();
        document.getElementById("account_connect")?.remove();

        this.modals.recoverRequest = new UIModal({
            container: "#ui-container",
            close: false,
            class: "account ui-modal-fullscreen ui-modal-vertical-center",
            id: "account_recover_request",
            icon: "cadenas-white.svg",
            title: "Récupération de mon mot de passe",
            undertitle: "Pour commencer saisissez votre adresse email",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false
        });

        const modal = document.getElementById("account_recover_request");
        const back = document.createElement("div");
        back.classList.add("account-create-back");
        back.setAttribute("role", "button");
        back.setAttribute("aria-label", "Retour");

        const backIcon = document.createElement("img");
        backIcon.src = "/images/back-white.svg";
        backIcon.alt = "";
        back.append(backIcon);
        back.addEventListener("click", () => {
            this.modals.recoverRequest.remove();
            this.renderConnect();
            this.renderConnectEmail({
                email: this.pendingPasswordRecoveryEmail
            });
        });
        modal?.append(back);

        this.forms.recoverRequest = new UIForm({
            app: this.app,
            container: this.getModalContentZoneSelector("account_recover_request"),
            form: {
                id: "form_account_recover_request",
                submit: () => this.submitPasswordRecoveryRequest(),
                content: [
                    {
                        type: "email",
                        label: "Email",
                        name: "recover_email",
                        value: email,
                        required: true
                    }
                ],
                buttons: [
                    {
                        type: "submit",
                        label: "Suivant",
                        class: "big"
                    }
                ]
            }
        });

        this.modals.recoverRequest.show();
        return true;
    }

    renderPasswordRecoveryConfirm() {
        new Logs("message", ["ACCOUNT RENDER PASSWORD RECOVERY CONFIRM"]);
        this.requireAppUI();

        document.getElementById("account_recover_confirm")?.remove();
        document.getElementById("account_recover_request")?.remove();

        this.modals.recoverConfirm = new UIModal({
            container: "#ui-container",
            close: false,
            class: "account ui-modal-fullscreen",
            id: "account_recover_confirm",
            icon: "cadenas-white.svg",
            title: "Récupération de mon mot de passe",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false
        });

        const modal = document.getElementById("account_recover_confirm");
        const back = document.createElement("div");
        back.classList.add("account-create-back");
        back.setAttribute("role", "button");
        back.setAttribute("aria-label", "Retour");

        const backIcon = document.createElement("img");
        backIcon.src = "/images/back-white.svg";
        backIcon.alt = "";
        back.append(backIcon);
        back.addEventListener("click", () => {
            this.modals.recoverConfirm.remove();
            this.renderPasswordRecoveryRequest();
        });
        modal?.append(back);

        const recoveryConfirmForm = {
            id: "form_account_recover_confirm",
            submit: () => this.submitPasswordRecoveryConfirm(),
            content: [
                {
                    type: "message",
                    text: "Saisissez votre nouveau mot de passe et le code qui vous a été envoyé par mail."
                },
                {
                    type: "password",
                    label: "Nouveau mot de passe",
                    name: "recover_p1",
                    autocomplete: "new-password",
                    required: true
                },
                {
                    type: "validation_code",
                    label: "Code de récupération",
                    name: "recover_code",
                    count: 6,
                    format: ["integer", "integer", "letter", "letter", "integer", "integer"],
                    required: true
                }
            ],
            buttons: [
                {
                    type: "submit",
                    label: "Enregistrer",
                    class: "big"
                }
            ]
        };

        this.forms.recoverConfirm = recoveryConfirmForm;
        this.forms.recoverConfirmInstance = new UIForm({
            app: this.app,
            container: this.getModalContentZoneSelector("account_recover_confirm"),
            form: recoveryConfirmForm
        });

        this.modals.recoverConfirm.show();
    }

    async submitPasswordRecoveryRequest() {
        new Logs("message", ["ACCOUNT SUBMIT PASSWORD RECOVERY REQUEST"]);

        this.app.UIModalLoadingShow("#account_recover_request");

        try {
            const form = this.formToObject("form_account_recover_request");
            this.pendingPasswordRecoveryEmail = form.recover_email || null;
            const response = await this.requestPasswordRecovery(form.recover_email);
            this.app.UIModalLoadingHide("#account_recover_request");

            if (this.isSuccess(response)) {
                this.modals.recoverRequest.remove();
                this.renderPasswordRecoveryConfirm();
                return response;
            }

            return this.notifyError(response, "Récupération du mot de passe");
        } catch (error) {
            this.app.UIModalLoadingHide("#account_recover_request");
            return this.notifyException(error, "Récupération du mot de passe");
        }
    }

    async submitPasswordRecoveryConfirm() {
        new Logs("message", ["ACCOUNT SUBMIT PASSWORD RECOVERY CONFIRM"]);

        const form = this.formToObject("form_account_recover_confirm");
        const code = this.normalizeCreateAccountValidationCode(form.recover_code);
        const validationCodeConfig = this.getFormElementConfig(this.forms.recoverConfirm, "recover_code");

        if (!this.isValidCreateAccountValidationCode(code, validationCodeConfig)) {
            new Notification({
                icon: "warning.svg",
                title: "Code de récupération",
                content: "Le code de récupération est incomplet ou invalide."
            });
            return false;
        }

        this.app.UIModalLoadingShow("#account_recover_confirm");

        try {
            const response = await this.confirmPasswordRecovery({
                ...form,
                recover_code: code
            });
            this.app.UIModalLoadingHide("#account_recover_confirm");

            if (this.isSuccess(response)) {
                new Notification({
                    icon: "check.svg",
                    title: "Mot de passe",
                    content: response.message,
                    class: "no-icon-filter"
                });
                this.modals.recoverConfirm.remove();
                this.renderConnect();
                return response;
            }

            return this.notifyError(response, "Mot de passe");
        } catch (error) {
            this.app.UIModalLoadingHide("#account_recover_confirm");
            return this.notifyException(error, "Mot de passe");
        }
    }

    async createAccount(data) {
        new Logs("message", ["ACCOUNT CREATE"]);

        return this.query("post", this.endpoints.create, this.withAuthToken(this.normalizeCreateAccountData(data)));
    }

    async getOrganisations(query = "", country = "") {
        return this.query("get", this.endpoints.organisations, this.withAuthToken({
            q: query,
            country,
            _silentErrors: true
        }));
    }

    async createOrganisation(name, country) {
        return this.query("post", this.endpoints.createOrganisation, this.withAuthToken({
            name,
            country
        }));
    }

    async validateCreateAccountEmail(uid, code) {
        new Logs("message", ["ACCOUNT VALIDATE CREATE ACCOUNT EMAIL"]);

        const deviceData = await this.getDeviceAuthData();
        const data = new FormData();

        data.set("uid", uid);
        data.set("code", this.normalizeCreateAccountValidationCode(code));
        data.set("id_device", deviceData.id_device);
        data.set("device_info", JSON.stringify(deviceData.device_info));

        const response = await this.query("post", this.endpoints.validateAccount, this.withAuthToken(data));
        await this.storeAuthTokens(response);

        return response;
    }

    async validateOrganisationMembership(uid) {
        return this.query("post", this.endpoints.validateOrganisationMember, this.withAuthToken({
            uid,
            _silentErrors: true
        }));
    }

    extractCreateAccountUid(response = null) {
        new Logs("message", ["ACCOUNT EXTRACT CREATE ACCOUNT UID"]);

        const result = response?.result || {};

        if (typeof result === "string") {
            return result;
        }

        return result.uid || result.user?.uid || result.uuid || result.user?.uuid || null;
    }

    normalizeCreateAccountValidationCode(code) {
        new Logs("message", ["ACCOUNT NORMALIZE CREATE ACCOUNT VALIDATION CODE"]);

        return String(code || "").replace(/\s/g, "");
    }

    getFormElementConfig(formConfig, elementName) {
        new Logs("message", ["ACCOUNT GET FORM ELEMENT CONFIG", elementName]);

        const elements = Array.isArray(formConfig?.content) ? formConfig.content : [];

        for (const element of elements) {
            if (element?.name === elementName) {
                return element;
            }

            if (Array.isArray(element?.content)) {
                const subElement = element.content.find((item) => item?.name === elementName);

                if (subElement) {
                    return subElement;
                }
            }
        }

        return null;
    }

    isValidCreateAccountValidationCode(code, config = null) {
        new Logs("message", ["ACCOUNT IS VALID CREATE ACCOUNT VALIDATION CODE", config]);

        const validationConfig = config || {};
        const format = Array.isArray(validationConfig.format) ? validationConfig.format : [];
        const count = Number.isInteger(validationConfig.count)
            ? validationConfig.count
            : Number(validationConfig.fields || validationConfig.length || format.length || 0);

        if (!count || String(code || "").length !== count) {
            return false;
        }

        return String(code || "").split("").every((character, index) => {
            const kind = format[index] || "text";

            if (kind === "integer") {
                return /^[0-9]$/.test(character);
            }

            if (kind === "letter") {
                return /^[A-Z]$/i.test(character);
            }

            if (kind === "alphanumeric") {
                return /^[A-Z0-9]$/i.test(character);
            }

            return character.length === 1;
        });
    }

    getCreationMethods() {
        new Logs("message", ["ACCOUNT GET CREATION METHODS"]);

        return ["email"];
    }

    getConnectionMethods() {
        new Logs("message", ["ACCOUNT GET CONNECTION METHODS"]);

        return this.getCreationMethods();
    }

    async update(data) {
        new Logs("message", ["ACCOUNT UPDATE"]);

        return this.query("post", this.endpoints.update, this.withAuthToken(data));
    }

    async getOrganisationMembers() {
        return this.query("get", this.endpoints.organisationMembers, this.withAuthToken({
            _silentErrors: true
        }));
    }

    async getCurrentUser() {
        const response = await this.query("get", this.endpoints.currentUser, {
            _silentErrors: true
        });
        const user = response?.result?.user || null;

        if (!user) {
            throw new Error("La réponse du compte courant ne contient aucun utilisateur.");
        }

        this.applyUser(user);
        await this.storage.set(this.storageKeys.user, user);

        return user;
    }

    showAccount(goto = "infos") {
        new Logs("message", ["ACCOUNT SHOW ACCOUNT", goto]);
        this.requireAppUI();

        if (!this.app.user) {
            new Notification({
                title: "Connectez-vous",
                icon: "warning.svg",
                content: "Vous devez vous connecter pour accéder à votre compte."
            });
            this.renderConnect();
            return false;
        }

        // HIDE UI AND MAP
        this.app.hideUI();
        this.app.hideMap();

        // ADD VAPOR THEME
        //$("body").addClass("vapor");

        document.getElementById("account")?.remove();

        this.modals.account = new UIModal({
            container: "#ui-container",
            close: true,
            id: "account",
            //icon: this.app.user.class === "premium" ? "account-premium.svg" : "account-white.svg",
            icon : null,
            title: "Mon compte",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false,
            class: "account ui-modal-fullscreen"
        });

        const zoneContent = this.getModalContentZone("account");
        const contentContainer = document.createElement("div");
        contentContainer.classList.add("ctn");
        zoneContent?.append(contentContainer);

        const container = "#account .ui-modal-content .ui-modal-zonecontent .ctn";
        $(container).append(`
            <div class='tabs'>
                <div class='tab infos on'><div class="tab-icon glass-icon"><img src="/images/account-grey.svg"></div><div class='tab-label'>Vos informations</div></div>
                <div class='tab security'><div class="tab-icon glass-icon"><img src="/images/cadenas-grey.svg"></div><div class='tab-label'>Sécurité</div></div>
                <div class='tab preferences'><div class="tab-icon glass-icon"><img src="/images/pref-grey.svg"></div><div class='tab-label'>Préférences</div></div>
            </div>
            <div class='panes'>
                <div class='pane infos on'></div>
                <div class='pane security'></div>
                <div class='pane preferences'></div>
            </div>
        `);

        $("#account .close").on("click", () => {
            this.app.showMap();
            //this.app.showUI();
            //$("body").removeClass("vapor");
        })

        this.renderAccountProfilePhoto();
        this.renderAccountInfosPane();
        this.renderAccountSecurityPane();
        this.renderAccountPreferencesPane();
        this.bindAccountTabs(container);
        this.showAccountTab(goto);
        window.setTimeout(() => this.modals.account?.show(), 800);
        return true;
    }

    renderAccountProfilePhoto() {
        new Logs("message", ["ACCOUNT RENDER ACCOUNT PROFILE PHOTO"]);

        const content = document.querySelector("#account .ui-modal-content");
        const zoneTitle = document.querySelector("#account .ui-modal-content > .ui-modal-zonetitle");

        if (!content || !zoneTitle) {
            return false;
        }

        const container = document.createElement("div");
        container.classList.add("account-profile-photo");
        content.insertBefore(container, zoneTitle);

        new UIFormElement({
            app: this.app,
            container,
            data: {
                type: "photo_profil",
                name: "photo",
                id: "photo_profil",
                value: this.app.user?.photo,
                accepted_files: ["image/jpeg", "image/png", "image/gif"],
                class: "ui-form-element-photo-profil",
                form: "form_account_update",
                required: false
            },
            idModal: "account"
        });

        return container;
    }

    renderAccountInfosPane() {
        new Logs("message", ["ACCOUNT RENDER ACCOUNT INFOS PANE"]);

        const user = this.app.user || {};
        this.app.UIForm("#account .pane.infos", {
            id: "form_account_update",
            submit: () => this.submitUpdateAccount(),
            content: [
                {
                    type: "text",
                    label: "Prénom",
                    name: "prenom",
                    value: user.prenom,
                    class: "ui-form-element-width-50",
                    required: true
                },
                {
                    type: "text",
                    label: "Nom",
                    name: "nom",
                    value: user.nom,
                    class: "ui-form-element-width-50",
                    required: true
                },
                {
                    type: "text",
                    label: "Organisme",
                    name: "organisme",
                    value: user.organisme,
                    class: "ui-form-element-width-100"
                },
                {
                    type: "text",
                    label: "Adresse",
                    name: "adresse1",
                    value: user.adresse1,
                    class: "ui-form-element-width-100",
                    required: true
                },
                {
                    type: "text",
                    label: "Complément d'adresse",
                    name: "adresse2",
                    value: user.adresse2,
                    class: "ui-form-element-width-100"
                },
                {
                    type: "text",
                    label: "Code postal",
                    name: "code_postal",
                    value: user.code_postal,
                    class: "ui-form-element-width-50",
                    required: true
                },
                {
                    type: "text",
                    label: "Ville",
                    name: "ville",
                    value: user.ville,
                    class: "ui-form-element-width-50",
                    required: true
                },
                {
                    type: "select",
                    label: "Pays",
                    name: "pays",
                    required: true,
                    values: this.app.config?.countries?.list || [],
                    value: user.pays,
                    class: "ui-form-element-width-100",
                    values_config: {
                        label: "label",
                        value: "iso-3166-1-alpha-2"
                    }
                },
                {
                    type: "email",
                    label: "Email",
                    name: "email",
                    value: user.email,
                    class: "ui-form-element-width-100",
                    required: true
                },
                {
                    type: "tel",
                    label: "Téléphone",
                    name: "tel",
                    value: user.tel,
                    class: "ui-form-element-width-100"
                }
            ],
            buttons: [
                {
                    type: "submit",
                    label: "Enregistrer",
                    class: "big"
                }
            ]
        });
    }

    renderAccountSecurityPane() {
        new Logs("message", ["ACCOUNT RENDER ACCOUNT SECURITY PANE"]);

        $("#account .pane.security").append(`
            <div class="explain">Gérez la sécurité et la suppression de votre compte.</div>
            <div class="buttons">
                <button type="button" id="account_change_password" class="ui-buttons big">Changer mon mot de passe</button>
                <button type="button" id="account_delete" class="ui-buttons delete big">Supprimer mon compte</button>
            </div>
        `);

        $("#account_change_password").click(() => this.openChangePasswordFromAccount());
        $("#account_delete").click(() => this.renderDeleteAccountConfirm());
    }

    openChangePasswordFromAccount() {
        new Logs("message", ["ACCOUNT OPEN CHANGE PASSWORD FROM ACCOUNT"]);

        this.app.UIModalRemove("#account");
        this.renderChangePassword();
    }

    renderAccountPreferencesPane() {
        new Logs("message", ["ACCOUNT RENDER ACCOUNT PREFERENCES PANE"]);

        const preferences = this.app.user.preferences || {};

        $("#account .pane.preferences").append(`<div class="explain">Ajustez vos préférences à vos envies.</div>`);

        this.app.UIForm("#account .pane.preferences", {
            id: "form_account_preferences",
            submit: () => this.submitUpdatePreferences(),
            content: [
                {
                    type: "checkbox",
                    label: "Mémoriser l'état de légende sur mon appareil",
                    name: "keep_legende",
                    values: 1,
                    value: preferences.keep_legende === false ? 0 : 1
                },
                {
                    type: "checkbox",
                    label: "Mémoriser la position de la carte",
                    name: "keep_mapcenter",
                    values: 1,
                    value: preferences.keep_mapcenter === false ? 0 : 1
                },
                {
                    type: "checkbox",
                    label: "Recevoir les mails d'informations et de mises à jour",
                    name: "accept_mail",
                    values: 1,
                    value: preferences.accept_mail === false ? 0 : 1
                }
            ],
            buttons: [
                {
                    type: "submit",
                    label: "Enregistrer",
                    class: "big"
                }
            ]
        });
    }

    bindAccountTabs(container) {
        new Logs("message", ["ACCOUNT BIND ACCOUNT TABS"]);

        $(`${container} .tabs .tab`).click((event) => {
            const tab = $(event.currentTarget).hasClass("security")
                ? "security"
                : $(event.currentTarget).hasClass("preferences")
                    ? "preferences"
                    : "infos";
            this.showAccountTab(tab);
        });
    }

    showAccountTab(tab) {
        new Logs("message", ["ACCOUNT SHOW ACCOUNT TAB"]);

        $("#account .tabs .tab").removeClass("on");
        $(`#account .tabs .tab.${tab}`).addClass("on");
        $("#account .panes .pane").removeClass("on");
        $(`#account .panes .pane.${tab}`).addClass("on");
        this.setAccountModalTitle(tab);
    }

    setAccountModalTitle(tab) {
        new Logs("message", ["ACCOUNT SET ACCOUNT MODAL TITLE", tab]);

        const titles = {
            infos: "Mon compte",
            security: "Sécurité",
            preferences: "Préférences"
        };

        const title = titles[tab] || titles.infos;
        const titleElement = document.querySelector("#account .ui-modal-zonetitle .ui-modal-zonetitle-title");
        if (titleElement) titleElement.textContent = title;
    }

    async submitUpdateAccount() {
        new Logs("message", ["ACCOUNT SUBMIT UPDATE ACCOUNT"]);

        this.app.UIModalLoadingShow("#account");

        try {
            const response = await this.update(new FormData(document.getElementById("form_account_update")));
            this.app.UIModalLoadingHide("#account");

            if (this.isSuccess(response)) {
                if (response.result) this.applyUser(response.result);
                this.app.navigation?.setAccountPhoto?.();
                new Notification({
                    icon: "check.svg",
                    title: "Mon compte",
                    content: response.message,
                    class: "no-icon-filter"
                });
                return response;
            }

            return this.notifyError(response, "Mon compte");
        } catch (error) {
            this.app.UIModalLoadingHide("#account");
            return this.notifyException(error, "Mon compte");
        }
    }

    async submitUpdatePreferences() {
        new Logs("message", ["ACCOUNT SUBMIT UPDATE PREFERENCES"]);

        this.app.UIModalLoadingShow("#account");

        try {
            const preferences = this.formToObject("form_account_preferences");
            preferences.keep_legende = preferences.keep_legende === "1";
            preferences.keep_mapcenter = preferences.keep_mapcenter === "1";
            preferences.accept_mail = preferences.accept_mail === "1";
            const response = await this.updatePreferences(preferences);
            this.app.UIModalLoadingHide("#account");

            if (this.isSuccess(response)) {
                this.applyUserPreferences(preferences);
                if (typeof this.app.clearLocalStorageDisabledPreferences === "function") {
                    await this.app.clearLocalStorageDisabledPreferences(preferences);
                }
                if (typeof this.app.setLocalStorage === "function") {
                    await this.app.setLocalStorage();
                }
                new Notification({
                    icon: "check.svg",
                    title: "Préférences",
                    content: response.message,
                    class: "no-icon-filter"
                });
                return response;
            }

            return this.notifyError(response, "Préférences");
        } catch (error) {
            this.app.UIModalLoadingHide("#account");
            return this.notifyException(error, "Préférences");
        }
    }

    async updatePreferences(preferences) {
        new Logs("message", ["ACCOUNT UPDATE PREFERENCES"]);

        return this.query("post", this.endpoints.updatePreferences, {
            preferences: JSON.stringify(preferences)
        });
    }

    async changePassword(data) {
        new Logs("message", ["ACCOUNT CHANGE PASSWORD"]);

        return this.query("post", this.endpoints.changePassword, data);
    }

    renderChangePassword() {
        new Logs("message", ["ACCOUNT RENDER CHANGE PASSWORD"]);

        this.app.UIModal({
            container: "#ui-container",
            close: true,
            id: "account_change_password_modal",
            icon: "cadenas-white.svg",
            title: "Nouveau mot de passe",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false,
            class: "account ui-modal-fullscreen"
        });

        this.getModalContentZone("account_change_password_modal");
        this.bindChangePasswordClose();

        this.app.UIForm(this.getModalContentZoneSelector("account_change_password_modal"), {
            id: "form_account_change_password",
            submit: () => this.submitChangePassword(),
            content: [
                {
                    type: "email",
                    label: "Email",
                    name: "username",
                    value: this.app.user?.email || "",
                    autocomplete: "username",
                    class: "account-password-username"
                },
                {
                    type: "password",
                    label: "Saisissez votre nouveau mot de passe",
                    name: "password",
                    autocomplete: "new-password",
                    required: true
                }
            ],
            buttons: [
                {
                    type: "submit",
                    label: "Enregistrer",
                    class: "big"
                }
            ]
        });

        this.app.UIModalShow("#account_change_password_modal");
    }

    bindChangePasswordClose() {
        new Logs("message", ["ACCOUNT BIND CHANGE PASSWORD CLOSE"]);

        const close = document.querySelector("#account_change_password_modal > .close");
        if (!close) {
            return false;
        }

        close.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.app.UIModalRemove("#account_change_password_modal");
            this.showAccount("security");
        }, true);

        return true;
    }

    async submitChangePassword() {
        new Logs("message", ["ACCOUNT SUBMIT CHANGE PASSWORD"]);

        this.app.UIModalLoadingShow("#account_change_password_modal");

        try {
            const response = await this.changePassword(this.formToObject("form_account_change_password"));
            this.app.UIModalLoadingHide("#account_change_password_modal");

            if (this.isSuccess(response)) {
                new Notification({
                    icon: "check.svg",
                    title: "Mot de passe",
                    content: response.message,
                    class: "no-icon-filter"
                });
                this.app.UIModalRemove("#account_change_password_modal");
                this.showAccount("security");
                return response;
            }

            return this.notifyError(response, "Mot de passe");
        } catch (error) {
            this.app.UIModalLoadingHide("#account_change_password_modal");
            return this.notifyException(error, "Mot de passe");
        }
    }

    async requestPasswordRecovery(email) {
        new Logs("message", ["ACCOUNT REQUEST PASSWORD RECOVERY"]);

        return this.query("post", this.endpoints.recoverPasswordRequest, this.withAuthToken({
            recover_email: email,
            _silentErrors: true
        }));
    }

    async confirmPasswordRecovery(data) {
        new Logs("message", ["ACCOUNT CONFIRM PASSWORD RECOVERY"]);

        return this.query("post", this.endpoints.recoverPasswordConfirm, this.withAuthToken({
            recover_p1: data.password || data.recover_p1,
            recover_code: data.code || data.recover_code,
            recover_email: data.email || data.recover_email || this.pendingPasswordRecoveryEmail,
            _silentErrors: true
        }));
    }

    async delete(data = null) {
        new Logs("warning", ["ACCOUNT DELETE"]);

        return this.query("post", this.endpoints.delete, this.withAuthToken({
            ...(data || {}),
            _silentErrors: true
        }));
    }

    renderDeleteAccountConfirm() {
        new Logs("message", ["ACCOUNT RENDER DELETE ACCOUNT CONFIRM"]);

        this.app.UIModalRemove("#account");

        this.app.UIModal({
            container: "#ui-container",
            close: null,
            id: "account_delete_confirm",
            icon: "warning-red.svg",
            title: "Supprimer mon compte",
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false,
            class: "account ui-modal-fullscreen"
        });

        this.app.UIForm(this.getModalContentZoneSelector("account_delete_confirm"), {
            id: "form_account_delete",
            submit: () => this.submitDeleteAccount(),
            content: [
                {
                    type: "message",
                    text: `Cette action supprimera votre compte.<br>Pour confirmer la suppression, saisissez le mot "JE CONFIRME" dans le champ ci-dessous.`
                },
                {
                    type: "text",
                    label: "Confirmation",
                    name: "confirm",
                    required: true
                }
            ],
            buttons: [
                {
                    type: "button",
                    label: "Annuler",
                    class: "cancel big",
                    fct: () => this.closeDeleteAccountConfirm()
                },
                {
                    type: "submit",
                    label: "Supprimer",
                    class: "delete big"
                }
            ]
        });

        //this.bindDeleteAccountClose();
        this.app.UIModalShow("#account_delete_confirm");
    }

    bindDeleteAccountClose() {
        new Logs("message", ["ACCOUNT BIND DELETE ACCOUNT CLOSE"]);

        const close = document.querySelector("#account_delete_confirm > .close");
        if (!close) {
            return false;
        }

        close.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.closeDeleteAccountConfirm();
        }, true);

        return true;
    }

    closeDeleteAccountConfirm() {
        new Logs("message", ["ACCOUNT CLOSE DELETE ACCOUNT CONFIRM"]);

        this.app.UIModalRemove("#account_delete_confirm");
        this.showAccount("security");

        return true;
    }

    async submitDeleteAccount() {
        new Logs("message", ["ACCOUNT SUBMIT DELETE ACCOUNT"]);

        const data = this.formToObject("form_account_delete");
        const confirmation = String(data.confirm || "").trim().toUpperCase();

        if (confirmation !== "JE CONFIRME") {
            new Notification({
                icon: "warning-red.svg",
                class: "no-icon-filter",
                title: "Confirmation",
                content: "Saisissez JE CONFIRME pour confirmer la suppression."
            });
            return false;
        }

        this.app.UIModalLoadingShow("#account_delete_confirm");

        try {
            const response = await this.delete(data);
            this.app.UIModalLoadingHide("#account_delete_confirm");

            if (this.isSuccess(response)) {
                this.renderDeleteAccountSuccess(response);
                await this.waitBeforeSignoutClose(4000);
                await this.signout({
                    notify: false
                });
                return response;
            }

            return this.notifyError(response, "Suppression du compte");
        } catch (error) {
            this.app.UIModalLoadingHide("#account_delete_confirm");
            return this.notifyException(error, "Suppression du compte");
        }
    }

    renderDeleteAccountSuccess(response) {
        new Logs("message", ["ACCOUNT RENDER DELETE ACCOUNT SUCCESS", response]);

        const modal = document.getElementById("account_delete_confirm");
        const container = this.getModalContentZone("account_delete_confirm");
        if (!container) {
            return false;
        }

        modal?.querySelector(".ui-modal-zoneicon")?.remove();
        modal?.querySelector(".ui-modal-zonetitle")?.remove();

        const success = document.createElement("div");
        success.classList.add("account-delete-success");

        const icon = document.createElement("img");
        icon.src = "/images/check.svg";
        icon.alt = "";
        icon.setAttribute("aria-hidden", "true");

        const message = document.createElement("div");
        message.classList.add("message");
        message.innerHTML = response?.message || "Votre demande de suppression de compte a été prise en compte.";

        success.append(icon, message);
        container.replaceChildren(success);

        return success;
    }

    async signout(options = {}) {
        new Logs("message", ["ACCOUNT SIGNOUT"]);

        const reload = options.reload !== false;
        const notify = options.notify !== false;
        const delay = options.delay ?? (notify ? 1200 : 0);
        const locale = this.app?.locale || document.documentElement.lang || "fr";
        const user = this.app?.user || {};
        const realname = user.realname || [user.prenom, user.nom].filter(Boolean).join(" ");

        try {
            if (notify) {
                new Notification({
                    icon: "hello-grey.svg",
                    title: translate("auth.signoutTitle", locale, {realname}),
                    content: translate("auth.signoutMessage", locale),
                    class: "no-icon-filter"
                });
            }

            if (delay > 0) {
                await this.waitBeforeSignoutClose(delay);
            }


            document.querySelectorAll("body .ui-modal").forEach((modal) => modal.remove());

            if (this.app && typeof this.app.removeLocalStorage === "function") {
                await this.app.removeLocalStorage();
            } else if (this.app?.config?.id_map) {
                await this.storage.removeMapState(this.app.config.id_map);
            }

            await this.storage.remove(this.storageKeys.token);
            await this.storage.remove(this.storageKeys.accessToken);
            await this.storage.remove(this.storageKeys.user);
            await this.storage.removeStorageToken();

            this.applyUser(null);

            if (this.app) {
                this.app.accessToken = null;
                this.app.refreshToken = null;
                this.app.token = null;
                this.app.hasFired = false;
            }

            if (reload && typeof window !== "undefined" && window.location) {
                window.location.reload();
                return true;
            }

            await this.app?.events?.list();

            if (options.boot !== false && this.app && typeof this.app.boot === "function") {
                return this.app.boot();
            }

            return true;
        } catch (error) {
            return this.notifyException(error, "Déconnexion");
        }
    }

    async waitBeforeSignoutClose(delay = 1200) {
        new Logs("message", ["ACCOUNT WAIT BEFORE SIGNOUT CLOSE", delay]);

        return new Promise((resolve) => {
            window.setTimeout(resolve, delay);
        });
    }

    async refreshAccessToken(refreshToken = null) {
        new Logs("message", ["ACCOUNT REFRESH ACCESS TOKEN"]);

        const token = refreshToken || await this.storage.getStorageToken();
        const response = await this.query("post", this.endpoints.refresh, {
            _authToken: token,
            refreshToken: token
        });

        await this.storeAuthTokens(response);

        return response;
    }

    async renderAuthenticationSuccess(response) {
        new Logs("message", ["ACCOUNT RENDER AUTHENTICATION SUCCESS"]);

        const modal = this.isCreateUserResponse(response)
            ? this.createUserSuccess(response)
            : this.loginSuccess(response);

        return modal;
    }

    createUserSuccess(response = null) {
        new Logs("message", ["ACCOUNT CREATE USER SUCCESS"]);


        const user = response?.result?.user || this.app?.user || {};
        const prenom = this.getUserPrenom(user);

        return this.renderSuccessModal({
            id: "account_create_success",
            icon : "check.svg",
            title: `Bonjour ${prenom}`,
            content: `Votre compte a été créé avec succés,<br> à vous les grands espaces !`,
            class: "ui-modal-vertical-center"
        });
    }

    loginSuccess(response = null) {
        new Logs("message", ["ACCOUNT LOGIN SUCCESS"]);

        const user = response?.result?.user || this.app?.user || {};
        const prenom = this.getUserPrenom(user);

        const modal = this.renderSuccessModal({
            id: "account_login_success",
            icon : "hello-color.svg",
            title: `Bonjour ${prenom}`,
            content: `Nous sommes contents de vous revoir.<br>La carte est en cours de chargement.`,
            class: "ui-modal-vertical-center"
        });

        return modal;
    }

    renderSuccessModal(options) {
        new Logs("message", ["ACCOUNT RENDER SUCCESS MODAL"]);


        this.removeAuthenticationModals();

        const modal = new UIModal({
            container: "#ui-container",
            close: true,
            class: `account ui-modal-fullscreen ${options.class || ""}`.trim(),
            id: options.id,
            icon: `/images/${options.icon || "check.svg"}`,
            title: options.title,
            content: "",
            remove_after_close: true,
            setDynamiquePosition: false
        });
        const container = this.getModalContentZone(options.id);
        const content = document.createElement("p");
        content.innerHTML = options.content;

        container?.append(content);
        modal.show();

        return modal;
    }

    removeAuthenticationModals() {
        new Logs("message", ["ACCOUNT REMOVE AUTHENTICATION MODALS"]);

        this.cleanupPostFireAuthContext();

        [
            "account_authentication_choice",
            "account_connect",
            "account_create",
            "account_create_success",
            "account_login_success"
        ].forEach((id) => {
            document.getElementById(id)?.remove();
        });
    }

    isCreateUserResponse(response) {
        new Logs("message", ["ACCOUNT IS CREATE USER RESPONSE"]);

        const message = response?.message || "";

        return /créé|cree|created/i.test(message);
    }

    getUserPrenom(user = {}) {
        new Logs("message", ["ACCOUNT GET USER PRENOM"]);

        return user.prenom;
    }

    withAuthToken(data = {}) {
        new Logs("message", ["ACCOUNT WITH AUTH TOKEN"]);

        const token = this.getAuthToken();

        if (!token) {
            return data;
        }

        if (data instanceof FormData) {
            data.set("_authToken", token);
            return data;
        }

        return {
            ...data,
            _authToken: token
        };
    }

    async withDeviceAuthData(data = {}) {
        new Logs("message", ["ACCOUNT WITH DEVICE AUTH DATA"]);

        const deviceData = await this.getDeviceAuthData();

        if (data instanceof FormData) {
            data.set("id_device", deviceData.id_device);
            data.set("device_info", JSON.stringify(deviceData.device_info));
            return data;
        }

        return {
            ...data,
            id_device: deviceData.id_device,
            device_info: deviceData.device_info
        };
    }

    getAuthToken() {
        new Logs("message", ["ACCOUNT GET AUTH TOKEN"]);

        return this.authToken
            || this.app?.startboot?.authToken
            || this.app?.config?.authToken
            || null;
    }

    async storeToken(token) {
        new Logs("message", ["ACCOUNT STORE TOKEN"]);

        await this.storage.set(this.storageKeys.token, token);
        if (this.app) this.app.token = token;
    }

    async storeAuthTokens(response) {
        new Logs("message", ["ACCOUNT STORE AUTH TOKENS"]);

        const result = response && response.result ? response.result : {};
        const refreshToken = response?.refreshToken || result.refreshToken || null;
        const accessToken = response?.accessToken || result.accessToken || null;
        const user = result.user || response?.user || null;

        if (typeof result === "string") {
            await this.storage.set(this.storageKeys.accessToken, result);

            if (this.app) {
                this.app.accessToken = result;
                this.app.token = result;
            }

            return true;
        }

        if (result.token) {
            await this.storeToken(result.token);
        }

        if (refreshToken) {
            await this.storage.setStorageToken(refreshToken);
        }

        if (accessToken) {
            await this.storage.set(this.storageKeys.accessToken, accessToken);
            if (this.app) {
                this.app.accessToken = accessToken;
                this.app.token = accessToken;
            }
        }

        if (refreshToken && this.app) {
            this.app.refreshToken = refreshToken;
        }

        if (user && this.app) {
            this.applyUser(user);
        }

        if (user) {
            await this.storage.set(this.storageKeys.user, user);
        }

    }

    async restoreSession() {
        const refreshToken = await this.storage.getStorageToken();

        this.clearRuntimeSession();

        if (!refreshToken) {
            await this.clearStoredSession();
            return false;
        }

        try {
            const response = await this.api.querySessionCheckEndpoint(refreshToken, this.endpoints.check);

            await this.storeAuthTokens(response);
            if (!this.app?.accessToken || !this.app?.user) {
                throw new Error("La réponse de vérification de session est incomplète.");
            }

            return true;
        } catch (error) {
            new Logs("warning", ["ACCOUNT SESSION RESTORE FAILED", error]);
            await this.clearStoredSession();
            this.clearRuntimeSession();
            return false;
        }
    }

    clearRuntimeSession() {
        if (!this.app) return;

        this.app.accessToken = null;
        this.app.refreshToken = null;
        this.app.token = null;
        this.app.user = null;
    }

    async clearStoredSession() {
        await Promise.all([
            this.storage.remove(this.storageKeys.token),
            this.storage.remove(this.storageKeys.accessToken),
            this.storage.remove(this.storageKeys.user),
            this.storage.removeStorageToken()
        ]);
    }

    applyUser(response) {
        new Logs("message", ["ACCOUNT APPLY USER"]);

        const user = response?.result?.user || response?.user || response || null;

        if (!this.app) {
            return false;
        }

        if (!user) {
            if (typeof this.app.setUser === "function") {
                this.app.setUser(null);
            } else {
                this.app.user = null;
            }

            return false;
        }

        if (typeof this.app.setUser === "function") {
            this.app.setUser(user);
        } else {
            this.app.user = user;
        }

        return true;
    }

    applyUserPreferences(preferences = {}) {
        new Logs("message", ["ACCOUNT APPLY USER PREFERENCES", preferences]);

        if (!this.app) {
            return false;
        }

        if (!this.app.user) {
            this.app.user = {};
        }

        this.app.user.preferences = {
            ...(this.app.user.preferences || {}),
            ...preferences
        };

        return this.app.user.preferences;
    }

    async getDeviceAuthData() {
        new Logs("message", ["ACCOUNT GET DEVICE AUTH DATA"]);

        const idDevice = await this.getDeviceId();
        const deviceInfo = await this.getDeviceInfo();

        return {
            id_device: idDevice,
            device_info: deviceInfo
        };
    }

    async getDeviceId() {
        new Logs("message", ["ACCOUNT GET DEVICE ID"]);

        let deviceId = await this.storage.get(this.storageKeys.deviceId);

        if (!deviceId) {
            deviceId = this.createUuid();
            await this.storage.set(this.storageKeys.deviceId, deviceId);
        }

        return deviceId;
    }

    async getDeviceInfo() {
        new Logs("message", ["ACCOUNT GET DEVICE INFO"]);

        const browserInfo = this.getBrowserInfo();
        const info = {
            mode: this.mode,
            platform: this.platform,
            isMobile: this.app?.isMobile === true || this.app?.device_info?.isMobile === true,
            browser: browserInfo.browser,
            browser_version: browserInfo.browserVersion,
            user_agent: window.navigator?.userAgent || null
        };

        return {
            ...info,
            model: null,
            manufacturer: null,
            os: browserInfo.os,
            os_version: browserInfo.osVersion,
            webview_version: null,
            is_virtual: null
        };
    }

    getBrowserInfo() {
        new Logs("message", ["ACCOUNT GET BROWSER INFO"]);

        const userAgent = window.navigator?.userAgent || "";
        const platform = window.navigator?.platform || "";
        const browserMatchers = [
            ["Edge", /Edg\/([\d.]+)/],
            ["Chrome", /Chrome\/([\d.]+)/],
            ["Safari", /Version\/([\d.]+).*Safari/],
            ["Firefox", /Firefox\/([\d.]+)/]
        ];
        const osMatchers = [
            ["iOS", /(?:iPhone|iPad|iPod).*OS ([\d_]+)/],
            ["Android", /Android ([\d.]+)/],
            ["macOS", /Mac OS X ([\d_]+)/],
            ["Windows", /Windows NT ([\d.]+)/],
            ["Linux", /Linux/]
        ];
        const browserMatch = browserMatchers.find(([, regexp]) => regexp.test(userAgent));
        const osMatch = osMatchers.find(([, regexp]) => regexp.test(userAgent) || regexp.test(platform));

        return {
            browser: browserMatch ? browserMatch[0] : "Unknown",
            browserVersion: browserMatch ? userAgent.match(browserMatch[1])?.[1] || null : null,
            os: osMatch ? osMatch[0] : "Unknown",
            osVersion: osMatch ? (userAgent.match(osMatch[1])?.[1] || null)?.replaceAll("_", ".") : null
        };
    }

    createUuid() {
        new Logs("message", ["ACCOUNT CREATE UUID"]);

        if (window.crypto && typeof window.crypto.randomUUID === "function") {
            return window.crypto.randomUUID();
        }

        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
            const random = Math.random() * 16 | 0;
            const value = char === "x" ? random : (random & 0x3 | 0x8);

            return value.toString(16);
        });
    }

    normalizeCreateAccountData(data) {
        new Logs("message", ["ACCOUNT NORMALIZE CREATE ACCOUNT DATA"]);

        return {
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            password: data.password || data.pass || null,
            pays: data.pays || null,
            organisme: data.organisme || null,
            _silentErrors: data._silentErrors || false
        };
    }

    formToObject(formId) {
        new Logs("message", ["ACCOUNT FORM TO OBJECT"]);

        const formData = new FormData(document.getElementById(formId));
        const data = {};

        formData.forEach((value, key) => {
            data[key] = value;
        });

        return data;
    }

    requireAppUI() {
        new Logs("message", ["ACCOUNT REQUIRE APP UI"]);

        if (!this.app || typeof this.app.UIModal !== "function" || typeof this.app.UIForm !== "function") {
            throw new Error("Account UI requires Budoevents UI helpers");
        }
    }

    isSuccess(response) {
        new Logs("message", ["ACCOUNT IS SUCCESS"]);

        return response && response.status === "OK";
    }

    notifyError(response, title = "Erreur") {
        new Logs("message", ["ACCOUNT NOTIFY ERROR"]);

        const message = response && response.message
            ? response.message
            : "Une erreur est survenue.";

        new Logs("error", [message]);
        new Notification({
            icon: "sad-red.svg",
            title,
            content: message
        });

        return false;
    }

    notifyException(error, title = "Erreur") {
        new Logs("message", ["ACCOUNT NOTIFY EXCEPTION"]);

        if (error?.apiHandled) {
            new Logs("error", [error.apiError || error]);
            return false;
        }

        new Logs("error", [error]);
        new Notification({
            icon: "sad-red.svg",
            title,
            content: error.message
        });

        return false;
    }

    query(method, endpoint, data = null) {
        new Logs("message", ["ACCOUNT QUERY"]);

        return this.queryInternal(method, endpoint, data);
    }

    queryInternal(method, endpoint, data = null) {
        new Logs("message", ["ACCOUNT QUERY INTERNAL"]);

        return new Promise((resolve, reject) => {
            if (!this.api) {
                reject(new Error("Account service requires an ApiClient instance"));
                return;
            }

            const showLoading = () => this.app?.simpleModal?.showLoading();
            const hideLoading = () => this.app?.simpleModal?.hideLoading();

            this.api.query(
                method,
                endpoint,
                data,
                showLoading,
                (response) => {
                    hideLoading();
                    resolve(response);
                },
                false
            ).then((success) => {
                if (!success) {
                    hideLoading();
                    const apiError = this.api.lastError || null;
                    const message = apiError?.message || `Account request failed: ${endpoint}`;
                    const error = new Error(message);
                    error.apiError = apiError;
                    error.apiHandled = !!apiError;
                    reject(error);
                }
            }).catch((error) => {
                hideLoading();
                reject(error);
            });
        });
    }

}

globalThis.Account = Account;
